/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1903755272 — CompletedScreen */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigate } from 'react-router-dom';
import { APP_ROUTES, IS_LOCAL_DEVELOPMENT } from '../../../../shared/constants';
import { isPlaceholderVm, useAppStore } from '../../../../shared/hooks/useAppStore';
import { usePageTracking } from '../../../../shared/hooks/usePageTracking';
import { usePurchaseActions } from '../../../../shared/hooks/usePurchaseActions';
import { useVmFeatures } from '../../../../shared/hooks/useVmFeatures';
import { Button, ButtonBox, ContentBox, PrimaryButton, SmallButton } from '../../../../shared/ui';
import { AnimatedIcon } from '../../../../shared/ui/AnimatedIcon/AnimatedIcon';
import tickAnimation from '../../../../shared/ui/AnimatedIcon/animations/tick.json';
import { useFeedbackActions } from '../../../feedback/hooks/useFeedbackActions';
import { GameBanner } from '../../../game';
import { LoyaltyProgress, useBalancePolling } from '../../../loyalty';
import { useReceiptActions } from '../../../service/hooks/useReceiptActions';
import { PredictionBubble } from '../../components/Prediction';
import { useReceiptPolling } from '../../hooks/useReceiptPolling';
import { ReceiptState, resolveReceiptState } from '../../lib/receipt-state';
import styles from './CompletedScreen.module.css';

const MARGIN_RIGHT = { marginRight: '2px' } as const;
const MARGIN_LEFT = { marginLeft: '2px' } as const;

interface ReceiptSlotProps {
    state: ReceiptState;
    /** Shares the row with "Repeat purchase" when feedback takes the primary button. */
    isSmall: boolean;
    onClick: () => void;
}

/**
 * The receipt button, or its loader while the receipt is in flight or owed.
 * `pending` keeps the button's place so the layout does not jump when the
 * receipt arrives (SWRN-2271).
 */
const ReceiptSlot = ({ state, isSmall, onClick }: ReceiptSlotProps) => {
    const { t } = useTranslation();
    if (state === 'none') return null;

    const SlotButton = isSmall ? SmallButton : Button;
    return (
        <SlotButton
            onClick={onClick}
            loading={state === 'pending'}
            loadingText={t('Receipt is being generated') ?? undefined}
            style={isSmall ? MARGIN_RIGHT : undefined}
        >
            {t('Get fiscal receipt')}
        </SlotButton>
    );
};

const CompletedScreen = () => {
    const { t } = useTranslation();
    const vm = useAppStore((s) => s.vm);
    const order = useAppStore((s) => s.order);
    const navigate = useNavigate();
    const { onRepeatPurchaseClickHandler } = usePurchaseActions();
    const { showFeedbackGlobal: showFeedback, showReceiptGlobal } = useVmFeatures();
    const { onFeedbackClickHandler, isFeedbackLoading } = useFeedbackActions();
    const { onGetReceiptHandler } = useReceiptActions();
    usePageTracking('completed');
    const { wasInFlight } = useReceiptPolling();
    useBalancePolling();

    const receiptState = resolveReceiptState(order, { hasFiscalReceipt: showReceiptGlobal, wasInFlight });
    const hasReceiptSlot = receiptState !== 'none';

    useEffect(() => {
        if (isPlaceholderVm(vm) && !IS_LOCAL_DEVELOPMENT) {
            // Redirect to the catch-all route to show the 404 screen when VM state is lost.
            navigate(`/${APP_ROUTES.notFound}`, { replace: false });
        }
    }, []);

    return (
        <div className={styles.screenWrapper}>
            <h1 className={styles.screenTitle}>{t('Thank you')}</h1>
            <ContentBox>
                <AnimatedIcon loop={false} animationData={tickAnimation} className={styles.canvasObject} />

                <ButtonBox>
                    {showFeedback && (
                        <PrimaryButton
                            type="button"
                            onClick={onFeedbackClickHandler}
                            disabled={isFeedbackLoading}
                            loading={isFeedbackLoading}
                        >
                            {t('Feedback')}
                        </PrimaryButton>
                    )}

                    <ReceiptSlot
                        state={receiptState}
                        isSmall={showFeedback}
                        onClick={onGetReceiptHandler}
                    />

                    {hasReceiptSlot && showFeedback ? (
                        <SmallButton
                            onClick={() => onRepeatPurchaseClickHandler('success')}
                            style={MARGIN_LEFT}
                        >
                            {t('Repeat_purchase')}
                        </SmallButton>
                    ) : (
                        <Button onClick={() => onRepeatPurchaseClickHandler('success')}>
                            {t('Repeat_purchase')}
                        </Button>
                    )}
                </ButtonBox>
            </ContentBox>

            {/* SWRN-1033 — ProstoBot prediction (shown instead of ads) */}
            <PredictionBubble />

            {/* Loyalty progress */}
            <LoyaltyProgress />

            {/* SWRN: loyalty mini-game — one try per purchase */}
            <GameBanner />
        </div>
    );
};

export { CompletedScreen };
